import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Table } from 'antd';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { RIDE_STATUSES } from '../../../constants/constants';
import { useWindowSize } from '../../../hooks/useWindowSIze';

import styles from './index.module.css';

export const BaseTable = ({ 
  columns, 
  onRowClick, 
  data, 
  isTCWorker, 
  components, 
  addHoverEffectToRowTable,
  horizontalScroll,
  // Optional tree table properties with safe defaults
  expandable = null,
  subTitle = null
}) => {
  const [, innerHeight] = useWindowSize();
  const expandedRowKeysSet = useMemo(() => new Set(expandable?.expandedRowKeys || []), [expandable?.expandedRowKeys]);
  const [verticalScroll, setVerticalScroll] = useState(300);

  useMemo(() => {
    if (subTitle) {
      setVerticalScroll(400);
    }

    return;
  }, [verticalScroll])

  const handleRowClassName = useCallback((record) => {
    // Build class list based on conditions
    const classNameList = [];
   
    // Status-based styling
    if (isTCWorker && record?.status === RIDE_STATUSES.PENDING) {
      classNameList.push(styles.newRequestRow);
    } else if (record?.status === RIDE_STATUSES.CLAIMED) {
      classNameList.push(styles.claimedRow);
    } else if (record?.status === RIDE_STATUSES.UNABLE_TO_FULFILL) {
      classNameList.push(styles.unableToFullfillRow);
    }
    
    // Add parent row styling if the row has children
    if (record.children && record.children.length > 0) {
      classNameList.push(styles.parentRow);
      if (expandedRowKeysSet.has(record.key)) {
        classNameList.push(styles.parentRowExpanded);
      }
    }
    // Add child row styling for nested organizations
    if (record.parentOrgId) {
      classNameList.push(styles.childRow);
    }
    
    return classNameList.join(' ');
  }, [expandedRowKeysSet, isTCWorker]);

  const onRow = useCallback((record) => (onRowClick ? { onClick: onRowClick(record) } : null), [onRowClick]);

  useEffect(() => {
    const tableBody = document.querySelector('.ant-table-body');

    if (data && tableBody) {
      tableBody.scrollTop = 0;
    }
    
    return;
  }, [data]);
  
  // Determine if data has tree structure
  const hasTreeData = useMemo(() => data?.some((item) => item.children && item.children.length > 0), [data]);
  
  // Only apply tree-specific classes if data has children
  const tableClassName = useMemo(
    () =>
      classNames({
        [styles.baseTable]: true,
        [styles.hoverOnRow]: addHoverEffectToRowTable,
        [styles.treeTable]: hasTreeData,
      }),
    [addHoverEffectToRowTable, hasTreeData],
  );

  const horizontalScrollValue = horizontalScroll ?? 'max-content';

  return (
    <Table
      className={tableClassName}
      pagination={false}
      columns={columns}
      dataSource={data}
      rowClassName={handleRowClassName}
      onRow={onRow}
      components={components}
      tableLayout={horizontalScroll ? 'fixed' : undefined}
      scroll={
        data?.length > 0
          ? { x: horizontalScrollValue, y: innerHeight - verticalScroll }
          : { x: horizontalScrollValue }
      }
      // If expandable is provided, use it, otherwise auto-detect tree data
      {...(expandable !== null ? { expandable } : hasTreeData ? { childrenColumnName: 'children' } : {})}
    />
  );
};

BaseTable.propTypes = {
  columns: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
  renderTableHead: PropTypes.func,
  onRowClick: PropTypes.func,
  data: PropTypes.array,
  dataLoaded: PropTypes.bool,
  isTCWorker: PropTypes.bool,
  components: PropTypes.object,
  addHoverEffectToRowTable: PropTypes.bool,
  horizontalScroll: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  // New prop types for tree table
  expandable: PropTypes.object,
};
